import { FormControlLabel, Radio, RadioGroup, Autocomplete, TextField, FormControl, LinearProgress } from '@mui/material'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dataTestIdPrefix } from './constants'
import { useActiveEvalFormGroups } from './hooks/useActiveEvalFormGroups'
import { useActiveEvalForms } from './hooks/useActiveEvalForms'

type SelectionType = 'single' | 'group'

interface FormSelectionInputElement extends Omit<HTMLInputElement, 'value'> {
  value: SelectionType,
}

const isFormGroup = (type: SelectionType) => {
  return type === 'group'
}

export default function EvaluationSelector() {
  const { t } = useTranslation()
  const [selectionType, setSelectionType] = useState<SelectionType>('single')
  const { formList, selectedForm, setSelectedForm, isLoading: isFormListLoading } = useActiveEvalForms()
  const { formGroupList, selectedFormGroup, setSelectedFormGroup, isLoading: isFormGroupListLoading } = useActiveEvalFormGroups()

  const isBusy = isFormListLoading || isFormGroupListLoading

  const handleSelectionTypeChange = (event: ChangeEvent<FormSelectionInputElement>) => {
    setSelectionType(event.target.value)
  }

  return (
    <FormControl fullWidth={true}>
      {isBusy && <LinearProgress color="secondary" data-testid={`${dataTestIdPrefix}-BusyIndicator`} />}
      <RadioGroup
        row
        name="EvalType"
        color="inherit"
        value={selectionType}
        onChange={handleSelectionTypeChange}
      >
        <FormControlLabel
          disabled={isBusy}
          sx={{ paddingRight: 5 }}
          value="single"
          control={<Radio data-testid={`${dataTestIdPrefix}singleForm`} />}
          label={t('form')}
        />
        <FormControlLabel
          disabled={isBusy}
          value="group"
          control={<Radio data-testid={`${dataTestIdPrefix}formGroup`} />}
          label={t('formGroup')}
        />
      </RadioGroup>
      {isFormGroup(selectionType)
        ? <Autocomplete
          disabled={isBusy}
          data-testid={`${dataTestIdPrefix}FormGroupSelect`}
          fullWidth={true}
          getOptionLabel={option => option.name}
          options={formGroupList}
          value={selectedFormGroup}
          onChange={(_, newValue) => setSelectedFormGroup(newValue)}
          renderInput={params =>
            <TextField {...params} label={t('chooseEvaluationFormGroup')} />
          }
        />
        : <Autocomplete
          disabled={isBusy}
          data-testid={`${dataTestIdPrefix}singleFormSelect`}
          fullWidth={true}
          getOptionLabel={option => option.name}
          options={formList}
          value={selectedForm}
          onChange={(_, newValue) => setSelectedForm(newValue)}
          renderInput={params =>
            <TextField {...params} label={t('chooseEvaluationForm')} />
          }
        />
      }
    </FormControl>
  )
}
